import { useCallback, useEffect } from 'react';
import { isInteractiveElement } from '@bynder-studio/misc';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import { type ControlShortcutMode } from 'packages/hooks/shortcuts/usePlaybackShortcuts';
import { useIsModalOpen } from '~/hooks/useIsModalOpen';

type Props = {
    toPrevShot: () => void;
    toNextShot: () => void;
    controlShortcutMode?: ControlShortcutMode;
};

// todo: Shots could be in a playback control hook, but only after
// implementing one stateless hook for control shots in all types of editors
export const useShotsShortcuts = ({ toPrevShot, toNextShot, controlShortcutMode = 'playhead' }: Props) => {
    const isModalOpen = useIsModalOpen();

    const handleKeyCodes = useCallback(
        (e) => {
            if (
                isInteractiveElement(e) ||
                !isCtrlKey(e) ||
                e.shiftKey ||
                controlShortcutMode !== 'playhead' ||
                isModalOpen
            ) {
                return;
            }

            switch (e.code) {
                case 'ArrowRight':
                    e.preventDefault();
                    toNextShot();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    toPrevShot();
                    break;
                default:
                    break;
            }
        },
        [controlShortcutMode, toNextShot, toPrevShot, isModalOpen],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyCodes);

        return () => {
            window.removeEventListener('keydown', handleKeyCodes);
        };
    }, [handleKeyCodes]);
};
